import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Toolbar,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  Upload as UploadIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  MoreVert as MoreVertIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  TableChart as SpreadsheetIcon,
  Code as CodeIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FolderShared as FolderSharedIcon,
  CloudUpload as CloudUploadIcon,
  TextFields as OCRIcon,
  Category as ClassifyIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fileService } from '../services/fileService';
import {
  File,
  FileSearchRequest,
  CreateFolderRequest,
  FileExplorerState,
  formatFileSize,
  getFileIcon,
  getClassificationColor,
  FILE_CATEGORIES
} from '../types/files';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`file-tabpanel-${index}`}
      aria-labelledby={`file-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const FileExplorerPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // State management
  const [state, setState] = useState<FileExplorerState>({
    currentFolderId: undefined,
    selectedFiles: [],
    viewMode: 'list',
    gridOptions: {
      showThumbnails: true,
      gridSize: 'medium',
      sortBy: 'name',
      sortOrder: 'asc'
    },
    listOptions: {
      showDetails: true,
      columns: [
        { id: 'name', label: 'Name', visible: true },
        { id: 'size', label: 'Size', visible: true },
        { id: 'type', label: 'Type', visible: true },
        { id: 'modified', label: 'Modified', visible: true },
        { id: 'classification', label: 'Classification', visible: true }
      ],
      sortBy: 'name',
      sortOrder: 'asc'
    },
    searchQuery: '',
    filters: {},
    isLoading: false
  });

  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    file: File;
  } | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Fetch files query
  const { data: filesData, isLoading, error, refetch } = useQuery({
    queryKey: ['files', state.currentFolderId, state.searchQuery, state.filters],
    queryFn: () => {
      const searchRequest: FileSearchRequest = {
        folderId: state.currentFolderId,
        query: state.searchQuery,
        ...state.filters
      };
      return fileService.getFiles(searchRequest);
    }
  });

  // Fetch breadcrumbs query
  const { data: breadcrumbs } = useQuery({
    queryKey: ['breadcrumbs', state.currentFolderId],
    queryFn: () => {
      if (!state.currentFolderId) return Promise.resolve([]);
      return fileService.getBreadcrumbs(state.currentFolderId);
    },
    enabled: !!state.currentFolderId
  });

  // Mutations
  const createFolderMutation = useMutation({
    mutationFn: (request: CreateFolderRequest) => fileService.createFolder(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setNotification({
        open: true,
        message: 'Folder created successfully',
        severity: 'success'
      });
      setCreateFolderOpen(false);
      setNewFolderName('');
    },
    onError: (error) => {
      setNotification({
        open: true,
        message: `Error creating folder: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: string) => fileService.deleteFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setNotification({
        open: true,
        message: 'File deleted successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setNotification({
        open: true,
        message: `Error deleting file: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const ocrMutation = useMutation({
    mutationFn: (fileId: string) => fileService.processOCR(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setNotification({
        open: true,
        message: 'OCR processing completed',
        severity: 'success'
      });
    }
  });

  const classifyMutation = useMutation({
    mutationFn: (fileId: string) => fileService.classifyFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setNotification({
        open: true,
        message: 'File classification completed',
        severity: 'success'
      });
    }
  });

  // Event handlers
  const handleFolderCreate = () => {
    if (newFolderName.trim()) {
      createFolderMutation.mutate({
        name: newFolderName.trim(),
        parentId: state.currentFolderId
      });
    }
  };

  const handleFileClick = (file: File) => {
    if (file.type === 'folder') {
      setState(prev => ({ ...prev, currentFolderId: file.id }));
    }
  };

  const handleFileDoubleClick = (file: File) => {
    if (file.type === 'file') {
      // Open file preview or download
      handleDownloadFile(file);
    }
  };

  const handleBreadcrumbClick = (folderId?: string) => {
    setState(prev => ({ ...prev, currentFolderId: folderId }));
  };

  const handleContextMenu = (event: React.MouseEvent, file: File) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      file
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleDownloadFile = async (file: File) => {
    try {
      const downloadInfo = await fileService.downloadFile(file.id);
      // In a real implementation, this would trigger a file download
      window.open(downloadInfo.downloadUrl, '_blank');
      setNotification({
        open: true,
        message: 'Download started',
        severity: 'info'
      });
    } catch {
      setNotification({
        open: true,
        message: 'Error downloading file',
        severity: 'error'
      });
    }
  };

  const handleProcessOCR = (file: File) => {
    ocrMutation.mutate(file.id);
    handleContextMenuClose();
  };

  const handleClassifyFile = (file: File) => {
    classifyMutation.mutate(file.id);
    handleContextMenuClose();
  };

  const handleDeleteFile = (file: File) => {
    if (window.confirm(`Are you sure you want to delete "${file.name}"?`)) {
      deleteFileMutation.mutate(file.id);
    }
    handleContextMenuClose();
  };

  const getFileIconComponent = (file: File) => {
    const iconName = getFileIcon(file);
    switch (iconName) {
      case 'Folder':
        return file.sharing?.isShared ? <FolderSharedIcon /> : <FolderIcon />;
      case 'PictureAsPdf':
        return <PdfIcon />;
      case 'Image':
        return <ImageIcon />;
      case 'Description':
        return <DocumentIcon />;
      case 'TableChart':
        return <SpreadsheetIcon />;
      case 'Code':
        return <CodeIcon />;
      default:
        return <FileIcon />;
    }
  };

  const renderFileList = () => {
    if (!filesData?.files) return null;

    return (
      <List>
        {filesData.files.map((file) => (
          <ListItem
            key={file.id}
            component="div"
            onClick={() => handleFileClick(file)}
            onDoubleClick={() => handleFileDoubleClick(file)}
            onContextMenu={(e) => handleContextMenu(e, file)}
            sx={{
              borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
              cursor: 'pointer'
            }}
          >
            <ListItemIcon>
              {getFileIconComponent(file)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">{file.name}</Typography>
                  {file.sharing?.isShared && (
                    <Chip
                      size="small"
                      icon={<ShareIcon />}
                      label="Shared"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {file.classification && (
                    <Chip
                      size="small"
                      label={FILE_CATEGORIES.find(c => c.value === file.classification?.category)?.label}
                      sx={{
                        backgroundColor: getClassificationColor(file.classification.category),
                        color: 'white'
                      }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography variant="body2" color="textSecondary">
                    {file.type === 'file' ? formatFileSize(file.size) : 'Folder'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Modified: {new Date(file.updatedAt).toLocaleDateString()}
                  </Typography>
                  {file.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {file.tags.slice(0, 3).map(tag => (
                        <Chip key={tag} size="small" label={tag} variant="outlined" />
                      ))}
                      {file.tags.length > 3 && (
                        <Chip size="small" label={`+${file.tags.length - 3}`} variant="outlined" />
                      )}
                    </Box>
                  )}
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={(e) => handleContextMenu(e, file)}
              >
                <MoreVertIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderFileGrid = () => {
    if (!filesData?.files) return null;

    return (
      <Grid container spacing={2}>
        {filesData.files.map((file) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={file.id}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { elevation: 4 },
                position: 'relative'
              }}
              onClick={() => handleFileClick(file)}
              onDoubleClick={() => handleFileDoubleClick(file)}
              onContextMenu={(e) => handleContextMenu(e, file)}
            >
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}>
                  {getFileIconComponent(file)}
                </Box>
                <Typography variant="body2" noWrap title={file.name}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {file.type === 'file' ? formatFileSize(file.size) : 'Folder'}
                </Typography>
                {file.classification && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      label={FILE_CATEGORIES.find(c => c.value === file.classification?.category)?.label}
                      sx={{
                        backgroundColor: getClassificationColor(file.classification.category),
                        color: 'white'
                      }}
                    />
                  </Box>
                )}
                {file.sharing?.isShared && (
                  <ShareIcon
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'primary.main'
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          File Explorer
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage and organize your documents with advanced file operations
        </Typography>
      </Box>

      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            component="button"
            variant="body1"
            onClick={() => handleBreadcrumbClick(undefined)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon fontSize="small" />
            Home
          </Link>
          {breadcrumbs?.map((crumb) => (
            <Link
              key={crumb.id}
              component="button"
              variant="body1"
              onClick={() => handleBreadcrumbClick(crumb.id)}
            >
              {crumb.name}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>

      {/* Toolbar */}
      <Paper sx={{ mb: 3 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search files and folders..."
            value={state.searchQuery}
            onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 300 }}
          />

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={state.viewMode}
            exclusive
            onChange={(_, newView) => {
              if (newView) setState(prev => ({ ...prev, viewMode: newView }));
            }}
            size="small"
          >
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flexGrow: 1 }} />

          {/* Action Buttons */}
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={() => setCreateFolderOpen(true)}
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Files
          </Button>
        </Toolbar>
      </Paper>

      {/* Main Content Area with Tabs */}
      <Paper sx={{ minHeight: 500 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Files" />
            <Tab label="Shared with me" />
            <Tab label="Recent" />
          </Tabs>
        </Box>

        {/* Files Tab */}
        <TabPanel value={tabValue} index={0}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="error">
                Error loading files
              </Typography>
              <Button onClick={() => refetch()} sx={{ mt: 2 }}>
                Retry
              </Button>
            </Box>
          ) : filesData?.files.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="textSecondary">
                No files found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Upload files or create folders to get started
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              {state.viewMode === 'list' ? renderFileList() : renderFileGrid()}
            </Box>
          )}
        </TabPanel>

        {/* Shared Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" color="textSecondary">
              Shared Files Coming Soon
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              This will show files shared with you by other users
            </Typography>
          </Box>
        </TabPanel>

        {/* Recent Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" color="textSecondary">
              Recent Files Coming Soon
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              This will show your recently accessed files
            </Typography>
          </Box>
        </TabPanel>
      </Paper>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.file.type === 'file' && [
          <MenuItem key="view" onClick={() => handleFileDoubleClick(contextMenu.file)}>
            <ListItemIcon><ViewIcon /></ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>,
          <MenuItem key="download" onClick={() => handleDownloadFile(contextMenu.file)}>
            <ListItemIcon><DownloadIcon /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>,
          <Divider key="divider1" />,
          <MenuItem key="ocr" onClick={() => handleProcessOCR(contextMenu.file)}>
            <ListItemIcon><OCRIcon /></ListItemIcon>
            <ListItemText>Process OCR</ListItemText>
          </MenuItem>,
          <MenuItem key="classify" onClick={() => handleClassifyFile(contextMenu.file)}>
            <ListItemIcon><ClassifyIcon /></ListItemIcon>
            <ListItemText>Classify File</ListItemText>
          </MenuItem>,
          <Divider key="divider2" />
        ]}
        <MenuItem onClick={() => {}}>
          <ListItemIcon><ShareIcon /></ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {}}>
          <ListItemIcon><EditIcon /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => contextMenu && handleDeleteFile(contextMenu.file)}>
          <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleFolderCreate();
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleFolderCreate}
            variant="contained"
            disabled={!newFolderName.trim() || createFolderMutation.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog Placeholder */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Files</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">File Upload Coming Soon</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Drag and drop file upload functionality will be implemented here
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FileExplorerPage;